import {
  ChangeDetectionStrategy,
  Component,
  input,
  model,
  output,
  computed,
  inject,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { translation } from '@cue/translations';
import { ReactiveFormsModule } from '@angular/forms';
import { TextboxComponent } from '@cue/assist/ui/inputs';
import { LoadingComponent } from '@cue/loading';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  cueAppsSort,
  cueArrowRight,
  cueClose,
  cueFilter,
  cueInfo,
  cueInfoNoCircle,
  cueMarker,
  cueQrCode,
  cueSignInAlt,
  cueSearch,
  cueArrowFullLeft,
  cueClock,
  cueUser,
  IconComponent,
} from '@cue/ui/icons';
import { LocalizedStringPipe } from '@cue/utilities';
import { ButtonComponent } from '@cue/ui';
import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DynamicScrollSizeDirective } from '@cue/virtual-scroll';
import { localized } from '@cue/translate';
import {
  Area,
  INavigationResourceItem,
  ListType,
  NavigationBasicInfo,
  NavigationNearestItem,
  ResourcePoiIdentificationCrate,
  StartTargetSelectionModeEnum,
} from '@cue/data';
import { SlicePipe } from '@angular/common';
import { rxResource } from '@angular/core/rxjs-interop';
import { StartTargetFilterComponent, TargetFilterData } from '../start-target-filter/start-target-filter.component';
import { AreaToHierarchyPipe } from '../../pipes/area-to-hierarchy.pipe';
import { PoiBooleanFilterPipe } from '../../pipes/poi-boolean-filter.pipe';
import { SearchResultPipe } from '../../pipes/search-result.pipe';
import { CanShowPoisPipe } from '../../pipes/can-show-pois.pipe';
import { BaseSelection } from '../base-selection';
import { ResourceListDataSourceFactory } from './resource-list-datasource';
import { ResourceEventDetailComponent } from '../resouce-event-detail/resource-event-detail.component';
import { filter, Subscription, takeUntil, tap } from 'rxjs';
import { SignalRService } from '@cue/api';
import { injectDestroy } from 'ngxtension/inject-destroy';

@Component({
  selector: 'cue-resource-selection',
  templateUrl: './resource-selection.component.html',
  styleUrls: ['./resource-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TextboxComponent,
    TranslocoPipe,
    ReactiveFormsModule,
    LoadingComponent,
    IconComponent,
    ButtonComponent,
    StartTargetFilterComponent,
    AreaToHierarchyPipe,
    PoiBooleanFilterPipe,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    DynamicScrollSizeDirective,
    LocalizedStringPipe,
    SearchResultPipe,
    CanShowPoisPipe,
    SlicePipe,
    ResourceEventDetailComponent,
  ],
  animations: [
    trigger('slideInTop', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-50%)' }),
        animate('300ms', style({ opacity: 1, transform: 'translateY(0%)' })),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'translateY(0%)' }),
        animate('300ms', style({ opacity: 0, transform: 'translateY(-50%)' })),
      ]),
    ]),
    trigger('opacity', [
      transition(':enter', [style({ opacity: 0 }), animate('300ms', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('300ms', style({ opacity: 0 }))]),
    ]),
    trigger('scale', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0)' }),
        animate(
          '300ms',
          style({
            opacity: 1,
            transform: 'scale(1)',
          })
        ),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'scale(1)' }),
        animate(
          '300ms',
          style({
            opacity: 0,
            transform: 'scale(0)',
          })
        ),
      ]),
    ]),
  ],
})
export class ResourceSelectionComponent extends BaseSelection implements OnInit, OnDestroy {
  private intervalId: number;
  showNavigationFilter = model<boolean>(false);
  terminator = injectDestroy();
  private signalRSubscriptionList: Subscription[] = [];
  protected listType = ListType;
  protected translation = translation;
  protected ICONS = {
    cueQrCode,
    cueArrowRight,
    cueFilter,
    cueMarker,
    cueAppsSort,
    cueClose,
    cueSignInAlt,
    cueInfo,
    cueInfoNoCircle,
    cueSearch,
    cueArrowFullLeft,
    cueClock,
    cueUser,
  };

  isKioskMode = computed(() => {
    return !!this.kioskId();
  });

  deploymentId = input<string>();

  availableAreaList = input<Area[]>();
  startData = input<{
    qrCodeId?: string;
    resourceId?: number;
  }>();
  targetResourceName = model<string>();
  targetResourceId = input<number>();
  targetQrCodeId = input<string>();
  useVirtualScroll = model<boolean>(false);

  targetSelected = output<INavigationResourceItem>();
  requestOpenItemDetail = output<ResourcePoiIdentificationCrate>();
  requestCloseTargetSelection = output<void>();
  signalRService = inject(SignalRService);

  factory = inject(ResourceListDataSourceFactory);
  resourceListDataSource = rxResource({
    params: () => {
      const appId = this.appId();
      const searchString = this.searchSignal();
      const selectedLanguage = this.selectedLanguage();

      return {
        appId: appId,
        languageCode: selectedLanguage,
        searchString: searchString,

        kioskId: null,
        deploymentId: null,
        selectedAreaIds: [],
        selectedCategoryIdList: [],
        selectedResourceTypeIds: [],
      };
    },
    stream: ({ params }) => this.factory.create(params),
  });

  resourceList = rxResource({
    params: () => {
      const deploymentId = this.deploymentId();
      const kioskId = this.kioskId();
      const appId = this.appId();
      const searchString = this.searchSignal();
      const selectedCategoryIdList = this.selectedCategoryIdList();
      const selectedAreaIdList = this.selectedAreaIdList();
      const selectedResourceTypeIdList = this.selectedResourceTypeIdList();
      const selectedLanguage = this.selectedLanguage();

      return {
        kioskId: kioskId,
        appId: appId,
        deploymentId: deploymentId,
        languageCode: selectedLanguage,
        searchString: searchString,
        selectedAreaIds: selectedAreaIdList,
        selectedCategoryIdList: selectedCategoryIdList,
        selectedResourceTypeIds: selectedResourceTypeIdList,
      };
    },
    stream: ({ params }) => this.navigationService.loadResourceNavigationList(params),
  });

  ngOnInit(): void {
    this.intervalId = setInterval(() => {
      this.resourceList.reload();
    }, 60000);
    this.bindSignalREvents();
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private bindSignalREvents() {
    this.signalRSubscriptionList.push(
      this.signalRService.listen<string>('event-action', true, '/MessageHub').subscribe((x) => {
        this.resourceList.reload();
      })
    );
  }

  navigateToResource(
    resourceDetail: NavigationBasicInfo | NavigationNearestItem,
    mouseEvent: MouseEvent,
    isFromNearestList: boolean
  ) {
    this.navigateDEP(resourceDetail, mouseEvent, isFromNearestList);
  }

  navigate(
    resourceDetail: NavigationNearestItem | NavigationBasicInfo,
    mouseEvent: MouseEvent,
    isFromNearest: boolean
  ) {
    if (resourceDetail.qrCodes?.length === 1 && resourceDetail.qrCodes[0].notNavigationPoint) {
      return;
    }
    if (resourceDetail.qrCodes && resourceDetail.qrCodes.filter((x) => !x.notNavigationPoint).length > 1) {
      const overlayIdx = this.overlaysIds().findIndex((x) => x === resourceDetail.resourceId);

      if (overlayIdx > -1) {
        this.removeOverlay(resourceDetail.resourceId);
      } else {
        this.addOverlay(resourceDetail.resourceId);
        return;
      }
    }
    this.navigateToResource(resourceDetail, mouseEvent, isFromNearest);
  }

  protected navigateDEP(
    resourceDetail: NavigationBasicInfo | NavigationNearestItem,
    event: MouseEvent,
    isFromNearestList: boolean
  ) {
    event.stopPropagation();

    let qrCodeIdToNavigate = null;

    if (resourceDetail.resourceId == null) {
      if (resourceDetail.qrCodeId) {
        qrCodeIdToNavigate = resourceDetail.qrCodeId;
      } else if ((resourceDetail as unknown as NavigationBasicInfo).qrCodes?.length == 1) {
        qrCodeIdToNavigate = (resourceDetail as unknown as NavigationBasicInfo).qrCodes[0].qrCodeId;
      }
    }

    this.targetSelected.emit({
      areaId: resourceDetail.areaId,
      qrCodeId: qrCodeIdToNavigate,
      name: localized(resourceDetail.name, this.translocoService),
      displayNameForApp: localized(resourceDetail.displayNameForApp, this.translocoService),
      resourceId: resourceDetail.resourceId,
      targetSelectionMode: isFromNearestList
        ? StartTargetSelectionModeEnum.SelectedFromNearestList
        : StartTargetSelectionModeEnum.SelectedFromList,
    });
    this.targetResourceName.set(
      localized(resourceDetail.displayNameForApp, this.translocoService) ||
        localized(resourceDetail.name, this.translocoService)
    );

    this.requestOpenItemDetail.emit({
      resourceId: resourceDetail.resourceId,
      qrCodeId: resourceDetail.qrCodeId,
    });
  }

  navigateToSpecificQR(resourceDetail: NavigationBasicInfo | NavigationNearestItem, qrCode: string, event: MouseEvent) {
    event.stopPropagation();
    const qrPointName = resourceDetail.qrCodes.find((x) => x.qrCodeId === qrCode)?.name;

    this.targetSelected.emit({
      areaId: resourceDetail.areaId,
      qrCodeId: qrCode,
      name: qrPointName,
      displayNameForApp: qrPointName,
      resourceId: null,
      targetSelectionMode: StartTargetSelectionModeEnum.SelectedFromList,
    });

    this.targetResourceName.set(localized(qrPointName, this.translocoService));

    this.requestOpenItemDetail.emit({
      resourceId: resourceDetail.resourceId,
      qrCodeId: resourceDetail.qrCodeId,
    });
  }

  override updateData(filter: TargetFilterData) {
    super.updateData(filter);
  }

  override clearCategoryFilter() {
    super.clearCategoryFilter();
  }

  override clearTypeFilter() {
    super.clearTypeFilter();
  }

  openItemDetail(detail: NavigationBasicInfo, mouseEvent: MouseEvent) {
    mouseEvent.stopPropagation();
    this.requestOpenItemDetail.emit({
      resourceId: detail.resourceId,
      qrCodeId: detail.qrCodes.length === 1 ? detail.qrCodes[0].qrCodeId : detail.qrCodeId,
    });
    this.closeQrPopup.emit();
  }

  protected changeShowFilter(showFilter: boolean) {
    this.showNavigationFilter.set(showFilter);
    this.closeQrPopup.emit();
  }

  closeQrPopup = output<void>();
  protected readonly Date = Date;
}
